/** biome-ignore-all lint/complexity/noStaticOnlyClass: Stateless API wrapper, static methods are sufficient. */
import { apiClient } from '../../ApiClient';
import type { ApiResult, UtilRequestConfig } from '../../types';
import type {
	Contract,
	ContractCaseWithRewards,
	ContractCaseWithRewardsResponse,
	ContractResponse,
	ContractRewardResponse,
	CRMActivitiesRequest,
	CRMActivitiesResponse,
	CRMPipelineRecordTask,
	CRMPipelineRecordTasksGroupedResponse,
	CreateAgentRequest,
	CreateAgentResponse,
	CreateCRMPipelineRecordTaskRequest,
	CreatePartnerEmployee,
	CreatePartnerEmployeeResult,
	CreatePipelineRecordRequest,
	CrmGroupedResponse,
	DeleteCRMPipelineRecordTaskRequest,
	GetAgentResponse,
	GetCrmGroupedRequest,
	PartnerEmployee,
	PartnerEmployeesRequest,
	PartnerEmployeesResponse,
	Pipeline,
	PipelineRecord,
	PipelineRecordDetail,
	PipelineRecordFieldUpdate,
	PipelineRecordFile,
	PipelineRecordImplementationFileFieldIDN,
	PipelineRecordImplementationFiles,
	PipelineRecordSyncScalarValue,
	PipelineRecordSyncStageValue,
	PipelineRecordSyncValue,
	UpdateCRMPipelineRecordTaskRequest,
	UpdatePartnerEmployee,
	UpdatePipelineRecordPayload,
	UpdatePipelineRecordRequest,
	UpdatePipelineRecordResponse,
	UploadPipelineRecordFileRequest
} from './types';
import {
	ColumnIDN,
	CrmPipelineStageMapper,
	PipelineRecordPayloadFieldIDN
} from './types';

type PipelineRecordStageFieldSchema = {
	format?: 'iso-date';
	stages: ColumnIDN[];
};

type PipelineRecordDetailTopLevelField = Extract<
	keyof PipelineRecord,
	| 'comment'
	| 'demo_date'
	| 'email'
	| 'full_name'
	| 'logo'
	| 'organization_name'
	| 'phone'
	| 'website'
>;

type ContractLike = Partial<Contract> & {
	contactTriggerType?: unknown;
	contractCaseDuration?: unknown;
	ownerPartnerIdn?: unknown;
	triggerType?: unknown;
};

export class CrmService {
	private static readonly PIPELINE_RECORD_FILE_FORM_FIELD = 'file';
	private static readonly PIPELINE_RECORD_FILE_FIELD_IDN_FORM_FIELD =
		'field_idn';
	private static readonly PIPELINE_RECORD_FILE_STAGE_IDN_FORM_FIELD =
		'stage_idn';

	private static readonly PIPELINE_RECORD_TOP_LEVEL_FIELD_IDNS: ReadonlySet<string> =
		new Set([
			PipelineRecordPayloadFieldIDN.StageIdn,
			PipelineRecordPayloadFieldIDN.PartnerMemberAssigneeIdn,
			PipelineRecordPayloadFieldIDN.PartnerAssigneeIdn,
			PipelineRecordPayloadFieldIDN.OrganizationCustomerIdn,
			PipelineRecordPayloadFieldIDN.PartnerCustomerIdn,
			PipelineRecordPayloadFieldIDN.PipelineIdn,
			PipelineRecordPayloadFieldIDN.Industry,
			PipelineRecordPayloadFieldIDN.LeadValue,
			PipelineRecordPayloadFieldIDN.ExpansionValue
		]);

	private static readonly PIPELINE_RECORD_NUMERIC_FIELD_IDNS: ReadonlySet<string> =
		new Set([
			PipelineRecordPayloadFieldIDN.LeadValue,
			PipelineRecordPayloadFieldIDN.ExpansionValue
		]);

	private static appendSearchParamValues(
		params: URLSearchParams,
		key: string,
		values: string[] | undefined
	): void {
		for (const value of values ?? []) {
			params.append(key, value);
		}
	}

	private static readonly PIPELINE_RECORD_STAGE_FIELD_SCHEMA: Record<
		string,
		PipelineRecordStageFieldSchema
	> = CrmService.createPipelineRecordStageFieldSchema();

	private static readonly PIPELINE_RECORD_DETAIL_TOP_LEVEL_FIELD_MAP: Partial<
		Record<PipelineRecordPayloadFieldIDN, PipelineRecordDetailTopLevelField>
	> = {
		[PipelineRecordPayloadFieldIDN.Comment]: 'comment',
		[PipelineRecordPayloadFieldIDN.DemoDate]: 'demo_date',
		[PipelineRecordPayloadFieldIDN.Email]: 'email',
		[PipelineRecordPayloadFieldIDN.FullName]: 'full_name',
		[PipelineRecordPayloadFieldIDN.Logo]: 'logo',
		[PipelineRecordPayloadFieldIDN.OrganizationName]: 'organization_name',
		[PipelineRecordPayloadFieldIDN.PhoneNumber]: 'phone',
		[PipelineRecordPayloadFieldIDN.OrganizationWebsite]: 'website'
	};

	private static pickFirstString(
		value: unknown,
		...fallbacks: unknown[]
	): string | undefined {
		if (typeof value === 'string' && value.length > 0) {
			return value;
		}

		for (const fallback of fallbacks) {
			if (typeof fallback === 'string' && fallback.length > 0) {
				return fallback;
			}
		}

		return undefined;
	}

	private static normalizeContractResponse(contract: ContractLike): Contract {
		return {
			...contract,
			contractOwnerPartnerIdn:
				CrmService.pickFirstString(
					contract.contractOwnerPartnerIdn,
					contract.ownerPartnerIdn
				) ?? null,
			contractTriggerType:
				CrmService.pickFirstString(
					contract.contractTriggerType,
					contract.contactTriggerType,
					contract.triggerType
				) ?? null,
			rewardCaseDuration:
				contract.rewardCaseDuration ?? contract.contractCaseDuration ?? null,
			updatedAt:
				CrmService.pickFirstString(contract.updatedAt, contract.createdAt) ?? ''
		} as Contract;
	}

	private static createPipelineRecordStageFieldSchema(): Record<
		string,
		PipelineRecordStageFieldSchema
	> {
		const fieldSchema: Record<string, PipelineRecordStageFieldSchema> = {};

		for (const stageIdn of Object.values(ColumnIDN)) {
			const stageFields = CrmPipelineStageMapper[stageIdn];

			for (const [fieldIdn, fieldDefinition] of Object.entries(stageFields)) {
				fieldSchema[fieldIdn] ??= {
					format: fieldDefinition.format,
					stages: []
				};

				fieldSchema[fieldIdn].stages.push(stageIdn);
			}
		}

		return fieldSchema;
	}

	private static isPipelineRecordStageValue(
		value: PipelineRecordSyncValue | undefined
	): value is PipelineRecordSyncStageValue {
		return value != null && typeof value === 'object' && !Array.isArray(value);
	}

	private static normalizePipelineRecordStageFieldValue(
		value: PipelineRecordSyncScalarValue | undefined,
		schema: PipelineRecordStageFieldSchema
	): PipelineRecordSyncScalarValue | undefined {
		if (schema.format !== 'iso-date') {
			return value;
		}

		if (typeof value !== 'string' || value.trim().length === 0) {
			return value;
		}
		const date = new Date(value);

		return date.toISOString().split('T')[0];
	}

	private static normalizePipelineRecordNumericFieldValue(
		fieldIdn: string,
		value: PipelineRecordSyncValue | undefined
	): PipelineRecordSyncValue | undefined {
		if (!CrmService.PIPELINE_RECORD_NUMERIC_FIELD_IDNS.has(fieldIdn)) {
			return value;
		}

		if (value === undefined || value === null) {
			return value;
		}

		if (typeof value === 'string' && value.trim().length === 0) {
			return null;
		}

		const numericValue =
			typeof value === 'string' ? Number(value.trim()) : value;

		if (!Number.isInteger(numericValue)) {
			throw new Error(`CRM field "${fieldIdn}" must be an integer`);
		}

		return numericValue;
	}

	private static resolvePipelineRecordFieldStage(
		fieldIdn: string,
		schema: PipelineRecordStageFieldSchema,
		stageIdn?: ColumnIDN
	): ColumnIDN {
		if (schema.stages.length === 1) {
			return schema.stages[0];
		}

		if (stageIdn != null && schema.stages.includes(stageIdn)) {
			return stageIdn;
		}

		throw new Error(`Stage context is required for CRM field "${fieldIdn}"`);
	}

	private static serializePipelineRecordFieldValue(
		fieldIdn: string,
		value: PipelineRecordSyncValue | undefined,
		stageIdn?: ColumnIDN
	): PipelineRecordSyncValue | undefined {
		if (
			value === undefined ||
			CrmService.PIPELINE_RECORD_TOP_LEVEL_FIELD_IDNS.has(fieldIdn)
		) {
			return CrmService.normalizePipelineRecordNumericFieldValue(
				fieldIdn,
				value
			);
		}

		const schema = CrmService.PIPELINE_RECORD_STAGE_FIELD_SCHEMA[fieldIdn];
		if (schema == null) {
			throw new Error(`Unsupported CRM pipeline record field "${fieldIdn}"`);
		}

		if (CrmService.isPipelineRecordStageValue(value)) {
			if (schema.stages.length === 1) {
				const schemaStageIdn = schema.stages[0];
				const stageValue = value[schemaStageIdn] ?? Object.values(value)[0];
				const normalizedStageValue =
					CrmService.normalizePipelineRecordStageFieldValue(stageValue, schema);

				if (normalizedStageValue === undefined) {
					return undefined;
				}

				return {
					[schemaStageIdn]: normalizedStageValue
				};
			}

			const serializedStageEntries = Object.entries(value).flatMap(
				([itemStageIdn, stageValue]) => {
					const normalizedStageValue =
						CrmService.normalizePipelineRecordStageFieldValue(
							stageValue,
							schema
						);

					return normalizedStageValue === undefined
						? []
						: [[itemStageIdn, normalizedStageValue]];
				}
			);

			return serializedStageEntries.length === 0
				? undefined
				: Object.fromEntries(serializedStageEntries);
		}

		const resolvedStageIdn = CrmService.resolvePipelineRecordFieldStage(
			fieldIdn,
			schema,
			stageIdn
		);
		const normalizedStageValue =
			CrmService.normalizePipelineRecordStageFieldValue(value, schema);

		if (normalizedStageValue === undefined) {
			return undefined;
		}

		return {
			[resolvedStageIdn]: normalizedStageValue
		};
	}

	private static serializePipelineRecordPayload(
		payload: UpdatePipelineRecordPayload,
		options?: {
			fieldUpdates?: PipelineRecordFieldUpdate[];
			stageIdn?: ColumnIDN;
		}
	): UpdatePipelineRecordPayload {
		const serializedPayload: UpdatePipelineRecordPayload = {};

		for (const [fieldIdn, value] of Object.entries(payload)) {
			const serializedValue = CrmService.serializePipelineRecordFieldValue(
				fieldIdn,
				value,
				options?.stageIdn ?? payload.stage_idn ?? undefined
			);

			if (serializedValue !== undefined) {
				serializedPayload[fieldIdn] = serializedValue;
			}
		}

		for (const field of options?.fieldUpdates ?? []) {
			const serializedValue = CrmService.serializePipelineRecordFieldValue(
				field.idn,
				field.value,
				field.stageIdn ?? options?.stageIdn ?? payload.stage_idn ?? undefined
			);

			if (serializedValue !== undefined) {
				serializedPayload[field.idn] = serializedValue;
			}
		}

		return serializedPayload;
	}

	private static serializePipelineRecordFile(
		request: UploadPipelineRecordFileRequest
	): FormData {
		const formData = new FormData();

		formData.append(CrmService.PIPELINE_RECORD_FILE_FORM_FIELD, request.file);

		if (request.fieldIdn != null) {
			formData.append(
				CrmService.PIPELINE_RECORD_FILE_FIELD_IDN_FORM_FIELD,
				request.fieldIdn
			);
		}
		if (request.stageIdn != null) {
			formData.append(
				CrmService.PIPELINE_RECORD_FILE_STAGE_IDN_FORM_FIELD,
				request.stageIdn
			);
		}

		return formData;
	}

	private static isPipelineRecordDetailFieldMissing(value: unknown): boolean {
		return (
			value == null || (typeof value === 'string' && value.trim().length === 0)
		);
	}

	private static isPipelineRecordImplementationFileFieldIdn(
		fieldIdn: string | null | undefined
	): fieldIdn is PipelineRecordImplementationFileFieldIDN {
		return (
			fieldIdn === PipelineRecordPayloadFieldIDN.FilledBoi ||
			fieldIdn === PipelineRecordPayloadFieldIDN.FilledDeclaration
		);
	}

	private static getPipelineRecordDetailImplementationFiles(
		record: PipelineRecordDetail
	): PipelineRecordImplementationFiles {
		return record.history
			.flatMap((historyStage) => {
				if (
					historyStage.stage_idn !== ColumnIDN.Implementation ||
					!Array.isArray(historyStage.files)
				) {
					return [];
				}

				return historyStage.files;
			})
			.reduce<PipelineRecordImplementationFiles>(
				(implementationFiles, file) => {
					if (
						!CrmService.isPipelineRecordImplementationFileFieldIdn(
							file.field_idn
						)
					) {
						return implementationFiles;
					}

					implementationFiles[file.field_idn] ??= [];
					implementationFiles[file.field_idn]?.push(file);
					return implementationFiles;
				},
				{}
			);
	}

	private static normalizePipelineRecordDetailTopLevelFields(
		normalizedRecordFields: Record<string, unknown>,
		historyFieldValues: Record<string, string>
	): void {
		for (const [fieldIdn, recordField] of Object.entries(
			CrmService.PIPELINE_RECORD_DETAIL_TOP_LEVEL_FIELD_MAP
		)) {
			if (recordField == null) {
				continue;
			}

			const historyFieldValue = historyFieldValues[fieldIdn];
			const normalizedFieldValue = normalizedRecordFields[fieldIdn];

			if (!CrmService.isPipelineRecordDetailFieldMissing(historyFieldValue)) {
				normalizedRecordFields[recordField] = historyFieldValue;
			} else if (
				CrmService.isPipelineRecordDetailFieldMissing(
					normalizedRecordFields[recordField]
				) &&
				!CrmService.isPipelineRecordDetailFieldMissing(normalizedFieldValue)
			) {
				normalizedRecordFields[recordField] = normalizedFieldValue;
			}

			if (
				CrmService.isPipelineRecordDetailFieldMissing(
					normalizedRecordFields[fieldIdn]
				) &&
				(!CrmService.isPipelineRecordDetailFieldMissing(
					normalizedRecordFields[recordField]
				) ||
					!CrmService.isPipelineRecordDetailFieldMissing(historyFieldValue))
			) {
				normalizedRecordFields[fieldIdn] =
					CrmService.isPipelineRecordDetailFieldMissing(
						normalizedRecordFields[recordField]
					)
						? historyFieldValue
						: normalizedRecordFields[recordField];
			}
		}
	}

	private static normalizePipelineRecordDetail<
		TRecord extends PipelineRecordDetail
	>(record: TRecord): TRecord {
		if (!Array.isArray(record.history)) {
			return record;
		}

		const historyFieldValues = Object.fromEntries(
			record.history.flatMap((historyStage) => {
				return historyStage.fields
					.filter((field) => field.value.trim().length > 0)
					.map((field) => [field.idn, field.value]);
			})
		);
		const implementationFiles =
			CrmService.getPipelineRecordDetailImplementationFiles(record);
		const normalizedRecord = {
			...record,
			...historyFieldValues,
			...(Object.keys(implementationFiles).length > 0
				? { implementation_files: implementationFiles }
				: {})
		};
		const normalizedRecordFields = normalizedRecord as Record<string, unknown>;

		CrmService.normalizePipelineRecordDetailTopLevelFields(
			normalizedRecordFields,
			historyFieldValues
		);

		return normalizedRecord as TRecord;
	}

	static getPipelines(config?: UtilRequestConfig): Promise<Pipeline[]> {
		return apiClient.get<Pipeline[]>('/v1/crm/pipelines', config);
	}

	static getContracts(
		config?: UtilRequestConfig
	): Promise<ApiResult<Contract[] | null>> {
		return apiClient.parseResponse(
			apiClient.get<ContractResponse[]>('/v1/crm/contracts', config),
			(data) => {
				if (!Array.isArray(data)) {
					console.warn('GET /v1/crm/contracts: data is not an array', data);
					return null;
				}

				return (apiClient.camelCasify(data) as ContractLike[]).map((contract) =>
					CrmService.normalizeContractResponse(contract)
				);
			}
		);
	}

	static getContractCases(
		contractId: string,
		config?: UtilRequestConfig
	): Promise<ApiResult<ContractCaseWithRewards[] | null>> {
		return apiClient.parseResponse(
			apiClient.get<ContractCaseWithRewardsResponse[]>(
				`/v1/crm/contracts/${contractId}/cases`,
				config
			),
			(data) => {
				if (!Array.isArray(data)) {
					console.warn(
						`GET /v1/crm/contracts/${contractId}/cases: data is not an array`
					);
					return null;
				}

				return apiClient.camelCasify(data) as ContractCaseWithRewards[];
			}
		);
	}

	static getContractRewards(
		contractId: string
	): Promise<ContractRewardResponse[]> {
		return apiClient.get<ContractRewardResponse[]>(
			`/v1/crm/contracts/${contractId}/rewards`
		);
	}

	static getGroupedPipelineRecords(
		request: GetCrmGroupedRequest,
		config?: UtilRequestConfig
	): Promise<CrmGroupedResponse> {
		const params = new URLSearchParams();

		params.set('pipeline_idn', request.pipeline_idn);

		for (const stageIdn of request.stage_idns) {
			params.append('stage_idns', stageIdn);
		}

		if (request.search) {
			params.set('search', request.search);
		}

		if (request.sort_by) {
			params.set('sort_by', request.sort_by);
		}

		if (request.partner_member_assignee_idn) {
			params.append(
				'partner_member_assignee_idn',
				request.partner_member_assignee_idn
			);
		}

		if (request.group_by) {
			params.set('group_by', request.group_by);
		}

		if (request.organization_name) {
			params.set('organization_name', request.organization_name);
		}

		if (request.primary_contact_name) {
			params.set('primary_contact_name', request.primary_contact_name);
		}

		if (request.phone_number) {
			params.set('phone_number', request.phone_number);
		}

		if (request.email) {
			params.set('email', request.email);
		}

		if (request.unassigned === true) {
			params.set('unassigned', 'true');
		}

		if (request.is_blocked !== undefined) {
			params.set('is_blocked', String(request.is_blocked));
		}

		if (request.priorities) {
			request.priorities.forEach((priority) => {
				params.append('priorities', priority);
			});
		}

		if (request.page != null) {
			params.set('page', String(request.page));
		}

		if (request.per != null) {
			params.set('per', String(request.per));
		}

		return apiClient.get<CrmGroupedResponse>(
			`/v2/bff/crm/pipeline-records?${params.toString()}`,
			config
		);
	}

	static updatePipelineRecord(
		request: UpdatePipelineRecordRequest
	): Promise<UpdatePipelineRecordResponse> {
		return apiClient
			.patch<UpdatePipelineRecordResponse>(
				`/v2/bff/crm/pipeline-records/${request.recordId}`,
				CrmService.serializePipelineRecordPayload(request.payload, {
					fieldUpdates: request.fieldUpdates,
					stageIdn: request.stageIdn
				}),
				{ silent: true }
			)
			.then((record) => CrmService.normalizePipelineRecordDetail(record));
	}

	static getPipelineRecord(recordId: string): Promise<PipelineRecordDetail> {
		return apiClient
			.get<PipelineRecordDetail>(`/v1/bff/crm/pipeline-records/${recordId}`)
			.then((record) => CrmService.normalizePipelineRecordDetail(record));
	}

	static getPipelineRecordTasksGrouped(
		recordId: string,
		config?: UtilRequestConfig
	): Promise<CRMPipelineRecordTasksGroupedResponse> {
		return apiClient.get<CRMPipelineRecordTasksGroupedResponse>(
			`/v2/bff/crm/pipeline-records/${recordId}/tasks?group_by=status`,
			config
		);
	}

	static createPipelineRecordTask(
		request: CreateCRMPipelineRecordTaskRequest
	): Promise<CRMPipelineRecordTask> {
		const { recordId, ...payload } = request;

		return apiClient.post<CRMPipelineRecordTask>(
			`/v2/bff/crm/pipeline-records/${recordId}/tasks`,
			payload
		);
	}

	static updatePipelineRecordTask(
		request: UpdateCRMPipelineRecordTaskRequest
	): Promise<CRMPipelineRecordTask> {
		const { recordId, taskId, ...payload } = request;

		return apiClient.patch<CRMPipelineRecordTask>(
			`/v2/bff/crm/pipeline-records/${recordId}/tasks/${taskId}`,
			payload,
			{ silent: true }
		);
	}

	static deletePipelineRecordTask(
		request: DeleteCRMPipelineRecordTaskRequest
	): Promise<boolean> {
		return apiClient.delete<boolean>(
			`/v2/bff/crm/pipeline-records/${request.recordId}/tasks/${request.taskId}`,
			{ silent: true }
		);
	}

	static uploadPipelineRecordFile(
		request: UploadPipelineRecordFileRequest
	): Promise<PipelineRecordFile> {
		return apiClient.post<PipelineRecordFile>(
			`/v1/crm/pipeline-records/${request.recordId}/files`,
			CrmService.serializePipelineRecordFile(request),
			{
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				noErrorMessage: true,
				onUploadProgress: request.onUploadProgress
			}
		);
	}

	static createAgent(
		request: CreateAgentRequest
	): Promise<CreateAgentResponse> {
		return apiClient.post<CreateAgentResponse>('/v1/crm/agents', request);
	}

	static getAgent(provisioningId: string): Promise<GetAgentResponse> {
		return apiClient.get<GetAgentResponse>(`/v1/crm/agents/${provisioningId}`);
	}

	static getPipelineRecordActivities(
		request: CRMActivitiesRequest,
		config?: UtilRequestConfig
	): Promise<CRMActivitiesResponse> {
		const params = new URLSearchParams({
			page: String(request.page),
			per: String(request.per)
		});

		CrmService.appendSearchParamValues(
			params,
			'actor_idns',
			request.actor_idns
		);

		if (request.created_from) {
			params.set('created_from', request.created_from);
		}

		if (request.created_to) {
			params.set('created_to', request.created_to);
		}

		CrmService.appendSearchParamValues(
			params,
			'activity_types',
			request.activity_types
		);
		CrmService.appendSearchParamValues(
			params,
			'field_idns',
			request.field_idns
		);

		return apiClient.get<CRMActivitiesResponse>(
			`/v2/bff/crm/pipeline-records/${request.recordId}/activities?${params.toString()}`,
			config
		);
	}

	static createPipelineRecord(
		request: CreatePipelineRecordRequest
	): Promise<PipelineRecordDetail> {
		const { fieldUpdates: requestFieldUpdates, ...payload } = request;

		return apiClient
			.post<PipelineRecordDetail>(
				'/v2/bff/crm/pipeline-records',
				CrmService.serializePipelineRecordPayload(payload, {
					fieldUpdates: requestFieldUpdates,
					stageIdn: request.stage_idn
				})
			)
			.then((record) => CrmService.normalizePipelineRecordDetail(record));
	}

	static async getPartnerEmployees(
		params: PartnerEmployeesRequest,
		config?: UtilRequestConfig
	) {
		const query = new URLSearchParams({
			page: String(params.page),
			per: String(params.per)
		});

		if (params.search) {
			query.set('search', params.search);
		}

		for (const stageIdn of params.available_stage_idns ?? []) {
			query.append('available_stage_idns', stageIdn);
		}

		if (params.tenant) {
			query.set('tenant', params.tenant);
		}

		return await apiClient.get<PartnerEmployeesResponse>(
			`/v2/customer/partner-contractors?${query.toString()}`,
			config
		);
	}

	static async getPartnerEmployeeByIdn(
		idn: string,
		config?: UtilRequestConfig
	) {
		return await apiClient.get<PartnerEmployee>(
			`/v2/customer/partner-contractors/by-idn/${idn}`,
			{
				...config,
				noErrorMessage: true
			}
		);
	}

	static createPartnerEmployee(
		request: CreatePartnerEmployee
	): Promise<CreatePartnerEmployeeResult> {
		return apiClient.post<CreatePartnerEmployeeResult>(
			'/v2/customer/partner-contractors',
			request,
			{
				noErrorMessage: true
			}
		);
	}

	static updatePartnerEmployee(
		request: UpdatePartnerEmployee
	): Promise<boolean> {
		return apiClient.patch<boolean>(
			`/v2/customer/partner-contractors/${request.targetAccountId}`,
			request
		);
	}

	static unlinkPartnerEmployee(accountId: string): Promise<boolean> {
		return apiClient.delete<boolean>(
			`/v2/customer/partner-contractors/${accountId}`
		);
	}
}
